import type { WaitingCard, WaitingReason, WaitingReasonKind } from "./types";

/**
 * Pure helpers over `GET /api/waiting`. Kept apart from the context that fetches
 * it so they can be tested without React.
 */

/** How a reason reads as a chip, in the order a row lists them. */
export const WAITING_REASON_LABELS: Record<WaitingReasonKind, string> = {
  question: "Question",
  reply: "Reply to send",
  flag: "Flagged",
};

const REASON_ORDER: WaitingReasonKind[] = ["question", "reply", "flag"];

/** The distinct kinds a card is waiting for, in chip order. */
export function reasonKinds(card: Pick<WaitingCard, "reasons">): WaitingReasonKind[] {
  const present = new Set(card.reasons.map((reason) => reason.kind));
  return REASON_ORDER.filter((kind) => present.has(kind));
}

/** Cards by task id, so a board's cards can be looked up without a scan each. */
export function indexWaiting(cards: WaitingCard[]): Map<string, WaitingCard> {
  return new Map(cards.map((card) => [card.taskId, card]));
}

/** How many waiting cards each board holds. */
export function countByBoard(cards: WaitingCard[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const card of cards) counts.set(card.boardId, (counts.get(card.boardId) ?? 0) + 1);
  return counts;
}

/** Only the items whose `id` is a waiting card — the board and queue filters. */
export function onlyWaiting<T extends { id: string }>(items: T[], byTask: Map<string, WaitingCard>): T[] {
  return items.filter((item) => byTask.has(item.id));
}

/** Oldest first — the one waiting longest is the one most owed an answer. */
export function sortWaiting(cards: WaitingCard[]): WaitingCard[] {
  return [...cards].sort((a, b) => new Date(a.since).getTime() - new Date(b.since).getTime());
}

/** A card's open questions, the ones the dialog can answer in place. */
export const questionsOf = (card: WaitingCard) =>
  card.reasons.filter((reason): reason is Extract<WaitingReason, { kind: "question" }> => reason.kind === "question");

/**
 * The Awaiting badge: one per row of the Awaiting page — each waiting card, and
 * each open question asked in an agent chat about no listed card. Core puts every
 * open question in exactly one of the two, so this agrees with the agents shown
 * as "waiting on you".
 */
export const awaitingTotal = (list: { cards: unknown[]; questions: unknown[] }): number => list.cards.length + list.questions.length;

/**
 * Whether an agent needs the user: any question still open. Read off
 * `openQuestions` rather than `status`, because an agent that is working again
 * can still have a question open from before — and "needs you" with nothing to
 * show was the bug. `status` stands in for non-admins, who get no questions.
 */
export const agentNeedsYou = (agent: { status: string; openQuestions?: unknown[] }): boolean =>
  (agent.openQuestions?.length ?? 0) > 0 || agent.status === "waiting";

/** The Agents badge: how many agents have a question open, from the same list as their pills. */
export const waitingAgentsTotal = (agents: Array<{ status: string; openQuestions?: unknown[] }>): number =>
  agents.filter(agentNeedsYou).length;

/** Past this a question is marked "old": probably moot, but never closed behind anyone's back. */
export const STALE_QUESTION_MS = 24 * 60 * 60 * 1000;

export const isStaleQuestion = (since: string, now: number): boolean => now - new Date(since).getTime() >= STALE_QUESTION_MS;

/** A question's first non-empty line, for a bar, a tooltip or a row. */
export const questionTitle = (body: string): string =>
  (body.split("\n").find((line) => line.trim() !== "") ?? body).trim().replace(/^#+\s*/, "");

/**
 * The My tasks badge: the owner's open cards over the live boards, which is the
 * set the My tasks page lists (`listTasks` without done cards or archived boards).
 * Board stats count the owner's row only, so anyone else gets no pre-count, and
 * zero is undefined so the badge hides rather than reading 0.
 */
export function myOpenTotal(userId: string, boards: Array<{ stats: { assignedToMe: number } }>): number | undefined {
  if (userId !== "me") return undefined;
  const total = boards.reduce((sum, board) => sum + board.stats.assignedToMe, 0);
  return total > 0 ? total : undefined;
}
